import { useFetch } from "~vue/api";
import type { FeedbackTarget, ListFeedbackIssuesResponse } from "~vue/types/feedback";

const cache = new Map<FeedbackTarget, Promise<string[]>>();

/**
 * The feedback-issue options for a target, fetched once per page load and shared across
 * every form instance. Callers can invoke this before opening the feedback dialog so the
 * list is already resolved when the form mounts — otherwise the fetch lands mid-way through
 * the dialog's enter animation and the content visibly reflows while it is still scaling in.
 *
 * A failed fetch REJECTS and is evicted from the cache, so the next call retries.
 * `execute(true)` is required for that: by default useFetch's execute swallows HTTP and
 * network failures and resolves to null, which would cache a fulfilled empty result forever.
 * A null/absent payload on a 2xx is treated the same way. Best-effort warmers must attach
 * their own no-op catch; the form owns the user-facing error and retry state.
 */
export function getFeedbackIssues(target: FeedbackTarget): Promise<string[]> {
    let issues = cache.get(target);
    if (!issues) {
        const request = useFetch(`/v1/feedback/feedback-issues/${target}`, { immediate: false }).get().json<ListFeedbackIssuesResponse>();
        issues = request
            .execute(true)
            .then(() => {
                const list = request.data.value?.issues;
                if (!list) {
                    throw new Error(`feedback-issues response for target "${target}" had no issues payload`);
                }
                return list;
            })
            .catch((error: unknown) => {
                cache.delete(target);
                throw error;
            });
        cache.set(target, issues);
    }
    return issues;
}
